import authSvc from '@efex-services/auth/authSvc';
import { RoleId } from '@efexteam/permission-manager';
import { selectProfileData } from '@pages/profile/store/selectors';
import { fetchUserProfile } from '@pages/profile/store';
import { ICreateMember } from '@pages/profile/types';
import {
  INITIAL_TABS_ARRAY,
  TABS,
} from '@pages/teamMember/addTeamMember/constants';
import {
  IAddMemberSchema,
  getFieldsForStep,
} from '@pages/teamMember/addTeamMember/schemas/addMember.schema';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import { useAppDispatch } from '@/store';
import { UseContinueBarProps, UseContinueBarService } from '../types';

const useContinueBar = ({
  activeTabs,
  goToActiveTab,
  setActiveTabsArray,
}: UseContinueBarProps): UseContinueBarService => {
  const dispatch = useAppDispatch();
  const userProfile = useSelector(selectProfileData);
  const { enterpriseId } = userProfile?.company || {};

  const { getValues, trigger, reset } =
    useFormContext<Partial<IAddMemberSchema>>();

  const {
    data,
    loading,
    error,
    invoke: createTeamMember,
  } = useApiCall((member) =>
    authSvc.createTeamMember(enterpriseId || '', member)
  );

  const validateStep = async (
    step: keyof typeof TABS | 'ALL'
  ): Promise<boolean> => {
    const fieldsToValidate = getFieldsForStep(step);
    return trigger(fieldsToValidate);
  };

  const goToAssignRoleStep = async () => {
    const isStepValid = await validateStep('ADD_MEMBER');
    if (isStepValid) {
      setActiveTabsArray({
        ...activeTabs,
        [TABS.ASSIGN_ROLE]: true,
      });
      goToActiveTab(TABS.ASSIGN_ROLE);
    }
  };

  const goToConfirmMemberStep = async () => {
    const isStepValid = await validateStep('ASSIGN_ROLE');
    if (isStepValid) {
      setActiveTabsArray({
        ...activeTabs,
        [TABS.CONFIRM_MEMBER]: true,
      });
      goToActiveTab(TABS.CONFIRM_MEMBER);
    }
  };

  const goToAddMemberStep = () => {
    goToActiveTab(TABS.ADD_MEMBER);
  };

  const getRequestPayload = (): Partial<ICreateMember> => ({
    userName: getValues().userName || '',
    email: getValues().email?.label || '',
    roleId: getValues().roleId as RoleId,
    authorizationConfirmed: getValues().authorizationTerms || false,
  });

  const createMember = async () => {
    try {
      const isValid = await validateStep('ALL');
      if (!isValid) return;
      await createTeamMember(getRequestPayload());
    } catch (err) {
      console.error('Error creating team member', err);
    }
  };

  const createNewMember = () => {
    reset();
    setActiveTabsArray(INITIAL_TABS_ARRAY);
    goToActiveTab(TABS.ADD_MEMBER);
  };

  useEffect(() => {
    if (data && !error && !loading) {
      dispatch(fetchUserProfile());
      goToConfirmMemberStep();
    }
    if (error) {
      console.error('error', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, loading]);

  return {
    loading,
    error,
    goToAssignRoleStep,
    goToAddMemberStep,
    createMember,
    createNewMember,
  };
};

export default useContinueBar;
